import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiErrorMessage } from '@/components/pass-transfers';
import { useToast } from '@/hooks/use-toast';
import { authedJson } from '@/lib/http';
import { queryClient } from '@/lib/queryClient';

interface StaffProduct {
  id: string;
  name: string;
  sessionCount: number;
  priceCents: number;
  isActive: boolean;
}

interface Holder {
  passId: string;
  name: string | null;
  email: string | null;
  boughtFrom: string;
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  heldInTransfers: number;
  expiresAt: string | null;
  expired: boolean;
  createdAt: string;
  isPaid: boolean;
}

interface PassHistory {
  pass: {
    id: string;
    totalSessions: number;
    usedSessions: number;
    remainingSessions: number;
    expiresAt: string | null;
    createdAt: string;
  };
  bookings: Array<{
    enrollmentId: string | null;
    sessionStart: string;
    classTitle: string;
    enrollmentStatus: string | null;
    punches: number;
  }>;
  adjustments: Array<{
    id: string;
    deltaPunches: number;
    kind: string;
    reason: string | null;
    createdAt: string;
    actorName: string;
  }>;
  transfers: Array<{
    id: string;
    punches: number;
    recipientEmail: string;
    status: string;
    createdAt: string;
  }>;
}

const KIND_LABELS: Record<string, string> = {
  restore: 'Punches restored',
  remove: 'Punches removed',
  booking_returned: 'Booking canceled, punch returned',
  session_canceled: 'Session canceled, punch returned',
  transfer_out: 'Sent to someone',
  transfer_in: 'Received from someone',
  transfer_returned: 'Transfer canceled, punches returned',
  expiry_changed: 'Expiry changed',
};

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : 'No expiry');
const fmtDateTime = (d: string) =>
  new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Customer-service tools for punch passes: pick a pass, see who holds it, and
 * restore/remove punches, change expiry, or return a booking's punch. Admins
 * see every pass; creators see only passes that pay them (enforced server-side).
 */
export function PassHoldersManager({ productId: fixedProductId }: { productId?: string }) {
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>();
  const [managingPassId, setManagingPassId] = useState<string | null>(null);

  const { data: productData } = useQuery<{ products: StaffProduct[] }>({
    queryKey: ['/api/passes/staff/products'],
    queryFn: () => authedJson('/api/passes/staff/products'),
    enabled: !fixedProductId,
  });
  const products = productData?.products ?? [];
  const productId = fixedProductId ?? selectedProductId ?? products[0]?.id;

  const { data: holderData, isLoading } = useQuery<{ holders: Holder[] }>({
    queryKey: ['/api/passes/staff/products', productId, 'holders'],
    queryFn: () => authedJson(`/api/passes/staff/products/${productId}/holders`),
    enabled: !!productId,
  });
  const holders = holderData?.holders ?? [];

  if (!fixedProductId && productData && products.length === 0) {
    return <p className="text-sm text-muted-foreground">No punch passes yet.</p>;
  }

  return (
    <div className="space-y-3">
      {!fixedProductId && products.length > 1 && (
        <Select value={productId} onValueChange={setSelectedProductId}>
          <SelectTrigger className="sm:max-w-sm">
            <SelectValue placeholder="Choose a pass" />
          </SelectTrigger>
          <SelectContent>
            {products.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
                {p.isActive ? '' : ' (not on sale)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : holders.length === 0 ? (
        <p className="text-sm text-muted-foreground">No one has bought this pass yet.</p>
      ) : (
        <div className="space-y-2">
          {holders.map(h => (
            <div
              key={h.passId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
              data-testid={`pass-holder-${h.passId}`}
            >
              <div className="min-w-0 text-sm">
                <p className="font-medium">{h.name || h.email || 'Unknown student'}</p>
                {h.name && h.email && <p className="text-muted-foreground">{h.email}</p>}
                <p className="text-muted-foreground">
                  {h.remainingSessions} of {h.totalSessions} left
                  {h.heldInTransfers > 0
                    ? ` · ${h.heldInTransfers} sent, not yet claimed`
                    : ''} · {h.expired ? 'Expired ' : 'Expires '}
                  {fmtDate(h.expiresAt)}
                  {h.isPaid ? '' : ' · free/gifted'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {h.expired && <Badge variant="secondary">Expired</Badge>}
                <Button size="sm" variant="outline" onClick={() => setManagingPassId(h.passId)}>
                  Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {managingPassId && (
        <ManagePassDialog
          passId={managingPassId}
          holder={holders.find(h => h.passId === managingPassId)}
          onClose={() => setManagingPassId(null)}
          onChanged={() =>
            void queryClient.invalidateQueries({
              queryKey: ['/api/passes/staff/products', productId, 'holders'],
            })
          }
        />
      )}
    </div>
  );
}

function ManagePassDialog({
  passId,
  holder,
  onClose,
  onChanged,
}: {
  passId: string;
  holder: Holder | undefined;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [delta, setDelta] = useState('1');
  const [reason, setReason] = useState('');
  const [expiry, setExpiry] = useState('');
  const [returningEnrollmentId, setReturningEnrollmentId] = useState<string | null>(null);

  const historyKey = ['/api/passes/staff/passes', passId, 'history'];
  const { data: history } = useQuery<PassHistory>({
    queryKey: historyKey,
    queryFn: () => authedJson(`/api/passes/staff/passes/${passId}/history`),
  });

  const afterChange = (title: string) => {
    toast({ title });
    setReason('');
    setReturningEnrollmentId(null);
    void queryClient.invalidateQueries({ queryKey: historyKey });
    onChanged();
  };
  const onError = (err: unknown) =>
    toast({ title: 'Could not save', description: apiErrorMessage(err), variant: 'destructive' });

  const post = (url: string, body: unknown) =>
    authedJson(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  const adjustMutation = useMutation({
    mutationFn: (sign: 1 | -1) =>
      post(`/api/passes/staff/passes/${passId}/adjust`, {
        delta: sign * parseInt(delta, 10),
        reason: reason.trim(),
      }),
    onSuccess: (_d, sign) => afterChange(sign > 0 ? 'Punches restored' : 'Punches removed'),
    onError,
  });

  const expiryMutation = useMutation({
    mutationFn: () =>
      post(`/api/passes/staff/passes/${passId}/expiry`, {
        // End of the chosen day, local time.
        expiresAt: expiry ? new Date(`${expiry}T23:59:59`).toISOString() : null,
        reason: reason.trim(),
      }),
    onSuccess: () => afterChange('Expiry updated'),
    onError,
  });

  const returnMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      post(`/api/passes/staff/bookings/${enrollmentId}/return-punches`, {
        reason: reason.trim(),
      }),
    onSuccess: () => afterChange('Booking canceled and punch returned'),
    onError,
  });

  const cancelTransferMutation = useMutation({
    mutationFn: (transferId: string) =>
      authedJson(`/api/passes/transfers/${transferId}/cancel`, { method: 'POST' }),
    onSuccess: () => afterChange('Transfer canceled; punches returned to this pass'),
    onError,
  });

  const count = parseInt(delta, 10);
  const hasReason = reason.trim().length >= 3;
  const busy =
    adjustMutation.isPending ||
    expiryMutation.isPending ||
    returnMutation.isPending ||
    cancelTransferMutation.isPending;
  const remaining = history?.pass.remainingSessions ?? holder?.remainingSessions ?? 0;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{holder?.name || holder?.email || 'Pass'}</DialogTitle>
          <DialogDescription>
            {remaining} of {history?.pass.totalSessions ?? holder?.totalSessions} punches left ·
            expires {fmtDate(history?.pass.expiresAt ?? holder?.expiresAt ?? null)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="pass-reason">Reason (required, shown in the history)</Label>
            <Textarea
              id="pass-reason"
              rows={2}
              placeholder="e.g. Class ran short — returning her punch"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="pass-delta">Punches</Label>
              <Input
                id="pass-delta"
                type="number"
                min={1}
                max={100}
                className="w-24"
                value={delta}
                onChange={e => setDelta(e.target.value)}
              />
            </div>
            <Button
              onClick={() => adjustMutation.mutate(1)}
              disabled={busy || !hasReason || !(count >= 1)}
            >
              Restore
            </Button>
            <Button
              variant="outline"
              onClick={() => adjustMutation.mutate(-1)}
              disabled={busy || !hasReason || !(count >= 1) || count > remaining}
            >
              Remove
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="pass-expiry">New expiry date</Label>
              <Input
                id="pass-expiry"
                type="date"
                value={expiry}
                onChange={e => setExpiry(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => expiryMutation.mutate()}
              disabled={busy || !hasReason || !expiry}
            >
              Set expiry
            </Button>
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Bookings with this pass</h3>
            {!history ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : history.bookings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No bookings yet.</p>
            ) : (
              history.bookings.map(b => (
                <div
                  key={`${b.enrollmentId}-${b.sessionStart}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-sm"
                >
                  <span>
                    {b.classTitle} · {fmtDateTime(b.sessionStart)}
                    {b.punches > 1 ? ` · ${b.punches} punches (with guests)` : ''}
                  </span>
                  {b.enrollmentId &&
                    b.enrollmentStatus === 'active' &&
                    (returningEnrollmentId === b.enrollmentId ? (
                      <span className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={busy || !hasReason}
                          onClick={() => returnMutation.mutate(b.enrollmentId!)}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReturningEnrollmentId(null)}
                        >
                          Keep
                        </Button>
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReturningEnrollmentId(b.enrollmentId)}
                      >
                        Cancel &amp; return punch
                      </Button>
                    ))}
                </div>
              ))
            )}
            {returningEnrollmentId && !hasReason && (
              <p className="text-xs text-muted-foreground">Add a reason above to confirm.</p>
            )}
          </section>

          {history && history.transfers.some(t => t.status === 'pending') && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Sent, not yet claimed</h3>
              {history.transfers
                .filter(t => t.status === 'pending')
                .map(t => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
                  >
                    <span>
                      {t.punches} to {t.recipientEmail}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => cancelTransferMutation.mutate(t.id)}
                    >
                      Cancel transfer
                    </Button>
                  </div>
                ))}
            </section>
          )}

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">History</h3>
            {history && history.adjustments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No adjustments.</p>
            ) : (
              history?.adjustments.map(a => (
                <div key={a.id} className="rounded-md border p-2 text-sm">
                  <p>
                    <span className="font-medium">{KIND_LABELS[a.kind] ?? a.kind}</span>
                    {a.deltaPunches !== 0 && ` (${a.deltaPunches > 0 ? '+' : ''}${a.deltaPunches})`}
                  </p>
                  <p className="text-muted-foreground">
                    {fmtDateTime(a.createdAt)} · {a.actorName}
                    {a.reason ? ` — ${a.reason}` : ''}
                  </p>
                </div>
              ))
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
